import type { EndpointDef } from '@skynet/data-helpers';

/**
 * Central endpoint definitions — single source of truth for the DEX Vaults API.
 *
 * This file drives:
 * - Enhanced API: computed endpoint keys in `enhanceEndpoints`
 * - Hook re-exports: clean name → generated name mapping
 * - MSW mock factories: HTTP method + URL path pattern
 */
export const DEX_VAULTS_API_ENDPOINTS = {
  convert: {
    generated: 'apiControllerConvert',
    method: 'post',
    path: '/web/convert',
  },
  getVaultBalance: {
    generated: 'apiControllerGetVaultBalance',
    method: 'get',
    path: '/web/balance/:vaultId',
  },
  getVaultDetails: {
    generated: 'apiControllerGetVault',
    method: 'get',
    path: '/web/vault/:vaultId',
  },
  getVaultSource: {
    generated: 'apiControllerGetVaultSource',
    method: 'get',
    path: '/web/vault/:vaultId/source',
  },
  toRaw: {
    generated: 'apiControllerConvertToRaw',
    method: 'get',
    path: '/web/toraw/:amount/:decimal',
  },
  withdraw: {
    generated: 'apiControllerWithdraw',
    method: 'post',
    path: '/web/withdraw',
  },
  refreshVault: {
    generated: 'apiControllerRefreshVault',
    method: 'post',
    path: '/web/refresh_vault/:vaultId',
  },
  listVaults: {
    generated: 'apiControllerGetVaults',
    method: 'get',
    path: '/web/vaults/:projectId/:all/:mine/:limit/:page',
  },
  addVault: {
    generated: 'apiControllerAddVault',
    method: 'post',
    path: '/web/add_vault',
  },
  updateVault: {
    generated: 'apiControllerUpdateVault',
    method: 'put',
    path: '/web/update_vault',
  },
  deleteVault: {
    generated: 'apiControllerDeleteVault',
    method: 'delete',
    path: '/web/delete_vault/:vaultId',
  },
  updateVaultStatus: {
    generated: 'apiControllerUpdateVaultStatus',
    method: 'put',
    path: '/web/update_status_vault/:vaultId/:action',
  },
  listVaultStats: {
    generated: 'apiControllerGetVaultStats',
    method: 'get',
    path: '/web/stats/:vaultId/:from/:till',
  },
  listExecutors: {
    generated: 'apiControllerGetExecutors',
    method: 'get',
    path: '/web/executors/:vaultId',
  },
} as const satisfies Record<string, EndpointDef>;

export type DexVaultsEndpointName = keyof typeof DEX_VAULTS_API_ENDPOINTS;
